import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import Card from "../../../../components/card";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import useEsTelefono from "../../../../utils/esTelefono";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";
import { Una } from "../promoApplications/filtrosPromos";
import { monto } from "../promoApplications/formato";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";
import "../promoApplications/promoApplications.css";
import "./promotions.css";
import { EditorPromo, TIPOS, TIPO_NOMBRE, tipoDe } from "./editorPromo";
import DetalleCatalogo from "./detalleCatalogo";
import { LA_DA, Pestana, Vigencia, pestanaDe, valorTexto } from "./formatoCatalogo";

/*
	Catálogo de Promos (sección 91, antes «Descuentos»; Módulos Claros, 2026-11-01). Solo el catálogo del plantel, con las piezas de
	Matrículas: Vigentes · Por Empezar · Vencidas (las desactivadas van con las Vencidas), pastillas Tipo y La Da, buscador, tabla con
	«Usos 30 Días» y «Descontado» (reemplazan la pestaña Reporte) y la ventana de cada promo (Cambiar, Copiar, Desactivar).
	Lo de cada alumna (acuerdos, grupos, Por Aprobar) y la Limpieza viven en Promociones (98). Quién edita lo decide el servidor (canEdit).
*/

const http = HttpManager.getInstance();
type Promo = any;
const PESTANAS: { key: Pestana; label: string }[] = [{ key: "vigentes", label: "Vigentes" }, { key: "porEmpezar", label: "Por Empezar" }, { key: "vencidas", label: "Vencidas" }];

const CatalogoPromos: React.FC = () => {
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [tab, setTab] = useState<Pestana>("vigentes");
	const [tipo, setTipo] = useState<string>("");
	const [laDa, setLaDa] = useState<string>("");
	const [search, setSearch] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [abierta, setAbierta] = useState<number | null>(null);
	const [editando, setEditando] = useState<number | "new" | null>(null);

	const load = () => {
		if (!campusId) return;
		http.paymentService.promotions(campusId).then((r: any) => { setData(r.data); setError(null); }).catch((e: any) => setError(serverError(e)));
	};
	useEffect(() => { setData(null); setAbierta(null); setEditando(null); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campusId]);

	const sym: string = data?.currencySymbol || "";
	const hoy: string = data?.today || "";
	const promos: Promo[] = useMemo(() => data?.promotions || [], [data]);
	const tiposUsados = useMemo(() => TIPOS.filter((t) => promos.some((p) => tipoDe(p) === t.id)).map((t, i) => ({ id: i + 1, key: t.id, name: TIPO_NOMBRE[t.id] })), [promos]);
	const laDaUsada = useMemo(() => Object.keys(LA_DA).filter((k) => promos.some((p) => (p.givenBy || "asesora") === k)).map((k, i) => ({ id: i + 1, key: k, name: LA_DA[k] })), [promos]);
	const tipoKey = tiposUsados.find((t) => String(t.id) === tipo)?.key || "";
	const laDaKey = laDaUsada.find((t) => String(t.id) === laDa)?.key || "";
	const pasa = (p: Promo) => {
		if (tipoKey && tipoDe(p) !== tipoKey) return false;
		if (laDaKey && (p.givenBy || "asesora") !== laDaKey) return false;
		const t = q.trim().toLowerCase();
		return !t || [p.name, p.code, p.conditions, p.courseSummary].some((x) => String(x || "").toLowerCase().includes(t));
	};
	const filas = promos.filter((p) => pestanaDe(p, hoy) === tab && pasa(p));
	const enTab = (k: Pestana) => promos.filter((p) => pestanaDe(p, hoy) === k).length;
	const promoAbierta = abierta !== null ? promos.find((p) => p.id === abierta) || null : null;
	const promoEditando = typeof editando === "number" ? promos.find((p) => p.id === editando) || null : null;
	const gris: React.CSSProperties = { fontSize: 13, color: "var(--wa-apagado)" };

	if (!campusId) return <div className="container-fluid waModulo" style={{ padding: 24 }}>Elige un plantel.</div>;

	const nombre = (p: Promo) => (
		<div style={{ minWidth: 0 }}>
			<b className="wpmNombre">{p.name}</b>{p.giftMissing ? <span className="wdChip wdChipOro" style={{ marginLeft: 6 }}>Sin Regalo</span> : null}
			<div style={gris}>{p.courseSummary || "Todos los cursos"}</div>
		</div>
	);
	const vacio = <div style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>Sin promociones.</div>;

	return (
		<div className="container-fluid waModulo waDescuentos waCatPromos" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px", maxWidth: "none" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>Catálogo de Promos</h4>
				{data?.canEdit ? (
					<button type="button" className="btn btn-primary waBotonGrande" onClick={() => setEditando("new")}>
						<FontAwesomeIcon icon={solid("plus")} />&nbsp;{movil ? "Agregar" : "Agregar Promoción"}
					</button>
				) : null}
			</div>
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}

			<Card>
				<div style={{ padding: movil ? "12px 12px 8px 12px" : "14px 18px 12px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
							{PESTANAS.map((t) => (
								<li className="nav-item" key={t.key}>
									<button type="button" className={"nav-link btn btn-sm " + (tab === t.key ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => { setTab(t.key); setAbierta(null); }}>
										{t.label}{data && t.key === "porEmpezar" && enTab("porEmpezar") ? <span className="waTabCuenta">{enTab("porEmpezar")}</span> : null}
									</button>
								</li>
							))}
						</ul>
						<div style={movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
							<SearchInputComponent search={search} onChangeSearch={setSearch} onClickSearch={() => setQ(search.trim())} onClearSearch={() => { setSearch(""); setQ(""); }} />
						</div>
					</div>

					<div className="waFmBarra">
						<div className="waFmBarraFecha" />
						<div className="waFmBarraListas">
							<Una titulo="Tipo" opciones={tiposUsados} valor={tipo} onChange={setTipo} />
							<Una titulo="La Da" opciones={laDaUsada} valor={laDa} onChange={setLaDa} />
							{tipo || laDa ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => { setTipo(""); setLaDa(""); }}>Limpiar Filtros</button> : null}
						</div>
					</div>

					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>
						{!data ? <div className="loader" /> : movil ? (
							filas.length ? (
								<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
									{filas.map((p) => (
										<div key={"p" + p.id} className="wpmTarjeta" role="button" tabIndex={0} onClick={() => setAbierta(p.id)} onKeyDown={(e) => { if (e.key === "Enter") setAbierta(p.id); }}>
											<div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>{nombre(p)}<div style={{ flexShrink: 0 }}><Vigencia p={p} hoy={hoy} /></div></div>
											<div style={{ fontSize: 14, marginTop: 4 }}><b>{valorTexto(p, sym)}</b> <span style={gris}>· {TIPO_NOMBRE[tipoDe(p)]}</span></div>
											<div style={gris}>{p.uses30 === 1 ? "1 Uso" : p.uses30 + " Usos"} en 30 Días · {monto(sym, p.discounted30)}</div>
										</div>
									))}
								</div>
							) : vacio
						) : (
							<table className="table table-hover wpmLista" style={{ marginBottom: 0 }}>
								<thead>
									<tr><th>Promoción</th><th>Tipo</th><th>Qué Da</th><th>Vigencia</th><th className="wpmNum">Usos 30 Días</th><th className="wpmNum">Descontado</th></tr>
								</thead>
								<tbody>
									{!filas.length ? <tr><td colSpan={6}>{vacio}</td></tr> : filas.map((p) => (
										<tr key={"p" + p.id} style={{ cursor: "pointer" }} onClick={() => setAbierta(p.id)}>
											<td>{nombre(p)}</td>
											<td>{TIPO_NOMBRE[tipoDe(p)]}<div style={gris}>{LA_DA[p.givenBy || "asesora"] || ""}</div></td>
											<td>{valorTexto(p, sym)}</td>
											<td style={{ whiteSpace: "nowrap" }}><Vigencia p={p} hoy={hoy} /></td>
											<td className="wpmNum">{p.uses30 ? p.uses30.toLocaleString("es-MX") : "-"}</td>
											<td className="wpmNum">{p.discounted30 > 0.004 ? monto(sym, p.discounted30) : "-"}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</Card>
			<div className="waSobreDock" />

			{promoAbierta && data ? (
				<DetalleCatalogo campusId={campusId} promo={promoAbierta} promos={promos} sym={sym} hoy={hoy} canEdit={!!data.canEdit} campuses={data.campuses || []}
					cursos={data.catalogCourses} onClose={() => setAbierta(null)} onCambiar={() => setEditando(promoAbierta.id)} onChanged={load} />
			) : null}
			{editando !== null && data ? (
				<EditorPromo campusId={campusId} promo={editando === "new" ? null : promoEditando} cursos={data.catalogCourses} promos={promos} sym={sym}
					onSaved={() => { setEditando(null); load(); }} onCancel={() => setEditando(null)} />
			) : null}
		</div>
	);
};

/*
	Los avisos ya enviados (Limpieza semanal, grupos, acuerdos) apuntan a /promotions?vista=…&plantel=…: esas vistas viven ahora en
	Promociones, así que el enlace viejo se manda ahí con el mismo plantel y sin dejar rastro en el historial (revisión B3).
*/
const VISTAS_DE_PROMOCIONES = ["limpieza", "grupos", "acuerdos"];
const PromotionsController: React.FC = () => {
	const location = useLocation();
	const vista = new URLSearchParams(location.search).get("vista") || "";
	if (VISTAS_DE_PROMOCIONES.includes(vista)) return <Navigate to={{ pathname: "/promoApplications", search: location.search }} replace />;
	return <CatalogoPromos />;
};

export default PromotionsController;
